import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Shield, ShieldAlert, Eye } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { getUsers, createUser, deleteUser, updateUser } from '../../services/api';
import { toast } from 'sonner';

const UsersAdmin = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ username: '', email: '', password: '', role: 'viewer' });

  const fetchData = async () => {
    try {
      const res = await getUsers();
      setUsers(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load users');
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await createUser(form);
      toast.success('User created');
      setDialogOpen(false);
      setForm({ username: '', email: '', password: '', role: 'viewer' });
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleRoleChange = async (username, newRole) => {
    try {
      await updateUser(username, { role: newRole });
      toast.success('Role updated');
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleDelete = async (username) => {
    if (!window.confirm(`Delete user "${username}"?`)) return;
    try {
      await deleteUser(username);
      toast.success('User deleted');
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const roleIcon = (role) => {
    switch(role) {
      case 'super_admin': return <ShieldAlert size={14} className="text-red-500" />;
      case 'admin': return <Shield size={14} className="text-blue-500" />;
      default: return <Eye size={14} className="text-gray-500" />;
    }
  };

  const roleColor = (role) => {
    switch(role) {
      case 'super_admin': return 'bg-red-50 text-red-700';
      case 'admin': return 'bg-blue-50 text-blue-700';
      default: return 'bg-gray-50 text-gray-600';
    }
  };

  return (
    <div className="space-y-6" data-testid="users-admin">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">User Management</h1>
          <p className="text-sm text-gray-500">{users.length} users</p>
        </div>
        <Button onClick={() => { setForm({ username: '', email: '', password: '', role: 'viewer' }); setDialogOpen(true); }} className="bg-[#2E3A6F]" data-testid="add-user-btn">
          <Plus size={16} className="mr-2" /> Add User
        </Button>
      </div>

      <div className="bg-white rounded-xl border">
        <div className="p-4 border-b bg-gray-50/50 rounded-t-xl">
          <div className="grid grid-cols-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">
            <span>Username</span>
            <span>Email</span>
            <span>Role</span>
            <span className="text-right">Actions</span>
          </div>
        </div>
        {loading ? (
          <div className="p-8 text-center text-gray-400">Loading...</div>
        ) : users.length === 0 ? (
          <div className="p-8 text-center text-gray-400">No users</div>
        ) : (
          users.map((user) => (
            <div key={user.username} className="grid grid-cols-4 items-center p-4 border-b last:border-0 hover:bg-gray-50 transition-colors" data-testid={`user-${user.username}`}>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-[#2E3A6F]/10 flex items-center justify-center text-[#2E3A6F] text-xs font-bold">
                  {user.username?.charAt(0).toUpperCase()}
                </div>
                <span className="font-medium text-sm text-gray-900">{user.username}</span>
              </div>
              <span className="text-sm text-gray-600">{user.email}</span>
              <div>
                <select
                  value={user.role}
                  onChange={(e) => handleRoleChange(user.username, e.target.value)}
                  className={`text-xs px-2 py-1 rounded-full font-medium border-0 cursor-pointer ${roleColor(user.role)}`}
                >
                  <option value="super_admin">Super Admin</option>
                  <option value="admin">Admin</option>
                  <option value="viewer">Viewer</option>
                </select>
              </div>
              <div className="text-right">
                <Button size="sm" variant="ghost" onClick={() => handleDelete(user.username)} className="text-red-500 hover:text-red-700 hover:bg-red-50 text-xs">
                  <Trash2 size={14} />
                </Button>
              </div>
            </div>
          ))
        )}
      </div>

      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="p-4 text-sm text-blue-800">
          <h4 className="font-semibold mb-1">Role Permissions</h4>
          <ul className="space-y-1 text-xs">
            <li><strong>Super Admin:</strong> Full access + user management</li>
            <li><strong>Admin:</strong> Manage products, categories, clients, banners, events</li>
            <li><strong>Viewer:</strong> View dashboard, enquiries, and dealerships only</li>
          </ul>
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Create User</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div><Label>Username *</Label><Input value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} required data-testid="user-username-input" /></div>
            <div><Label>Email *</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required /></div>
            <div><Label>Password *</Label><Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required minLength={6} /></div>
            <div>
              <Label>Role</Label>
              <select className="w-full h-9 rounded-md border border-input px-3 text-sm" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
                <option value="viewer">Viewer (Read-only)</option>
                <option value="admin">Admin (Full CMS)</option>
                <option value="super_admin">Super Admin (All access)</option>
              </select>
            </div>
            <div className="flex gap-3 pt-2">
              <Button type="submit" className="flex-1 bg-[#2E3A6F]" data-testid="user-submit-btn">Create User</Button>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UsersAdmin;
